import { HttpClient, HttpHeaders } from "@angular/common/http";
import { throwError, Observable, BehaviorSubject, of } from "rxjs";
import { Injectable } from "@angular/core";
import { tap, catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";

interface TokenResp {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  permittedMenu: any;

  private domain = environment.apiUrl.replace(/\/$/, '') + '/';
  public apiurl: any;
  headers = new HttpHeaders()
    .set("Content-Type", "application/json")
    .set("Accept", "application/json");
  httpOptions = { headers: this.headers };
  activeurl: any;
  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);
  private refreshTimerId: any = null;
  private readonly REFRESH_BEFORE_EXPIRY_SECONDS = 60;

  constructor(private http: HttpClient, private route: Router) {}

  private handleError(error: any) {
    return throwError(error);
  }

  loginUser(user: any): Observable<any> {
    this.apiurl = this.domain + `api/login`;
    return this.http.post<any>(this.apiurl, user).pipe(
      tap(res => this.setTokens(res.data))
    );
  }

  refresh(refreshToken: string): Observable<any> {
    return this.http.post<any>(`${this.domain}api/refresh`, { refresh_token: refreshToken }).pipe(
      tap(res => {
        const payload = res?.data ?? res;
        if (payload && (payload.access_token || payload.expires_in)) {
          const tokenResp: TokenResp = {
            access_token: payload.access_token ?? payload.accessToken,
            refresh_token: payload.refresh_token ?? payload.refreshToken,
            expires_in: payload.expires_in ?? payload.expiresIn ?? payload.expires ?? 0
          };
          this.setTokens(tokenResp);
        }
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${this.domain}api/logout`, {}).pipe(
      tap(() => this.clearTokens())
    );
  }

  setTokens(resp: TokenResp): void {
    const expiresAt = Date.now() + (resp.expires_in || 0) * 1000;
    localStorage.setItem('access_token', resp.access_token);
    localStorage.setItem('token', resp.access_token);
    if (resp.refresh_token) {
      localStorage.setItem('refresh_token', resp.refresh_token);
    }
    localStorage.setItem('expires_at', String(expiresAt));
    this.refreshSubject.next(resp.access_token);
    this.scheduleRefreshFromExpiry();
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getAccessTokenExpiry(): number | null {
    const exp = localStorage.getItem('expires_at');
    return exp ? Number(exp) : null;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  clearTokens(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('user');
    localStorage.removeItem('user_name');
    this.clearRefreshTimer();
    this.refreshSubject.next(null);
  }

  isAccessTokenExpired(): boolean {
    const exp = localStorage.getItem('expires_at');
    if (!exp) return true;
    return Date.now() > Number(exp);
  }

  passwordChange(user: any, type: any): Observable<any> {
    this.apiurl = this.domain + type;
    return this.http.post<any>(this.apiurl, user).pipe(
      tap(), catchError(this.handleError)
    );
  }

  /** Primary auth check — used by sidebar, guards */
  loggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  /** Alias so guards using isLoggedIn() also work */
  isLoggedIn(): boolean {
    return this.loggedIn();
  }

  adminLoggedIn(): boolean {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'admin';
  }

  firstLogin(): boolean {
    return !!localStorage.getItem('is_first_login');
  }

  employeeLoggedIn(): boolean {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'employee';
  }

  clientLoggedIn(): boolean {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'client';
  }

  companyLoggedIn(): boolean {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'company';
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  logoutUser(): void {
    this.clearTokens();
    this.route.navigate(['/login']);
  }

  /** Called on app startup to resume the token refresh scheduler */
  startup(): void {
    const access = this.getAccessToken();
    const exp    = localStorage.getItem('expires_at');
    if (access && exp) {
      this.scheduleRefreshFromExpiry();
    }
  }

  async checkPermission(): Promise<boolean> {
    const userType      = localStorage.getItem('user_type');
    const permittedMenu = this.permittedMenu?.data?.menus ?? [];
    localStorage.setItem('is_create', 'no');
    localStorage.setItem('is_modify', 'no');
    localStorage.setItem('is_remove', 'no');
    localStorage.setItem('menu_key', '');
    const menu: any = permittedMenu.filter(
      (val: any) => ('/' + userType + '/' + val.menu_link) === this.activeurl
    );
    if (menu.length !== 0) {
      localStorage.setItem('is_create', menu[0].is_create === 1 ? 'yes' : 'no');
      localStorage.setItem('is_modify', menu[0].is_modify === 1 ? 'yes' : 'no');
      localStorage.setItem('is_remove', menu[0].is_remove === 1 ? 'yes' : 'no');
      localStorage.setItem('menu_key', menu[0].menu_key ?? '');
      return (menu[0].is_remove === 1 || menu[0].is_read === 1 ||
              menu[0].is_modify === 1 || menu[0].is_create === 1);
    }
    return false;
  }

  async PemissionMenus(activeurl: string): Promise<void> {
    this.activeurl = activeurl;
    await this.ApiPemissionMenus();
  }

  ApiPemissionMenus(): Promise<void> {
    this.apiurl = `${this.domain}api/permission/menu`;
    const token   = localStorage.getItem('token') || '';
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + token);
    return this.http.get<any>(this.apiurl, { headers }).toPromise()
      .then(res => { this.permittedMenu = res; })
      .catch(err => console.error(err));
  }

  forceRefresh(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      this.route.navigate(['/login']);
      return throwError(() => new Error('No refresh token'));
    }

    if (this.refreshInProgress) {
      return this.refreshSubject.pipe(
        filter(t => t != null),
        take(1),
        switchMap(token => of(token!))
      );
    }

    this.refreshInProgress = true;
    this.refreshSubject.next(null);

    return this.refresh(refreshToken).pipe(
      switchMap((res: any) => {
        const payload   = res?.data ?? res;
        const newAccess = payload?.access_token ?? payload?.accessToken ?? this.getAccessToken();
        this.refreshSubject.next(newAccess ?? null);
        return of(newAccess ?? '');
      }),
      catchError(err => {
        this.clearTokens();
        this.route.navigate(['/login']);
        return throwError(() => err);
      }),
      finalize(() => { this.refreshInProgress = false; })
    );
  }

  private scheduleRefreshFromExpiry(): void {
    this.clearRefreshTimer();
    const expStr = localStorage.getItem('expires_at');
    if (!expStr) return;

    const expiryMs        = Number(expStr);
    const refreshBeforeMs = this.REFRESH_BEFORE_EXPIRY_SECONDS * 1000;
    let   delayMs         = expiryMs - Date.now() - refreshBeforeMs;

    if (delayMs <= 0) {
      this.forceRefresh().subscribe({ next: () => {}, error: () => {} });
      return;
    }

    const MAX_TIMEOUT = 2147483647;
    if (delayMs > MAX_TIMEOUT) delayMs = MAX_TIMEOUT;

    this.refreshTimerId = setTimeout(() => {
      this.forceRefresh().subscribe({ next: () => {}, error: () => {} });
    }, delayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }
}
